import { findOptionByValue } from "@base/utils";
import { currencies } from "@base/utils/constants";
import { WithAnalytics } from "@base/utils/hoc/withSendPageAnalytics";
import Button from "@components/atoms/button";
import DropDown from "@components/atoms/dropDown";
import TextField from "@components/atoms/input";
import Header from "@components/molecules/header";
import Layout from "@components/molecules/layout";
import LocationSearchBox from "@components/molecules/locationSearchBox";
import { SuggestionProps } from "@components/molecules/locationSearchBox/locationSearchBox.type";
import { StripeModal } from "@components/molecules/stripe-modal";
import SwitchWrapper from "@components/molecules/switch-wrapper";
import TransactionFeeDisplay from "@components/molecules/transaction-fee";
import { LoaderSnackbarWrapperActions } from "@components/organisms/LoaderSnackbarWrapper/LoaderSnackbarWrapper.slice";
import { Box } from "@mui/material";
import {
  LocalizationProvider,
  MobileDateTimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoItem } from "@mui/x-date-pickers/internals/demo";
import dayjs, { Dayjs } from "dayjs";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { ConnectedProps, connect } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import uuid4 from "uuid4";
import * as Yup from "yup";

import { homeActions } from "../home/home.slice";
import { transactionsActions } from "../transactions/transactions.slice";
import { gameActions } from "./game.slice";
import {
  Content,
  FirstForm,
  Form,
  FormWrapper,
  PaymentOptions,
  SecondForm,
} from "./game.styled";

import { ReactComponent as RightArrow } from "@assets/images/arrows/arrow-right.svg";

type ReduxProps = ConnectedProps<typeof connector>;

const Game = ({
  createGame,
  getLocation,
  getGameTransactionFee,
  setAlert,
  retrieveLocation,
  userHasStripeAccount,
}: ReduxProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const sessionToken = uuid4();
  const game = JSON.parse(localStorage.getItem("gameToBeCreated")!);

  const [contentRender, setContentRender] = useState({
    firstForm: true,
    secondForm: false,
  });
  const [isCash, setIsCash] = useState<boolean>(true);
  const [locationSuggestions, setLocationSuggestions] = useState<
    SuggestionProps[]
  >([]);
  const [stripeAccount, setStripeAccount] = useState<any>(null);
  const [showStripeButton, setShowStripeButton] = useState<any>(null);
  const [isBankTransfer, setIsBankTransfer] = useState<boolean>(false);
  const [isOnlinePayment, setIsOnlinePayment] = useState<boolean>(false);
  const [isAvailableOnMarketplace, setIsAvailableOnMarketplace] =
    useState<boolean>(false);
  const [showFeeCalc, setShowFeeCalc] = useState<boolean>(isOnlinePayment);

  const formik = useFormik({
    initialValues: {
      title: game?.firstForm?.title || "",
      location: game?.firstForm?.location || "",
      address: game?.firstForm?.address || {},
      date: null,
      duration: game?.firstForm?.duration || null,
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Required"),
      location: Yup.string().required("Required"),
      date: Yup.string().required("Required"),
      duration: Yup.object().required("Required"),
    }),
    onSubmit: () => {},
  });

  const form = useFormik({
    initialValues: {
      info: game?.secondForm?.info || "",
      chat: game?.secondForm?.chat || "",
      maxPlayers: game?.secondForm?.maxPlayers || 12,
      currency: game?.secondForm?.currency || "GBP",
      fee: game?.secondForm?.fee || 6,
    },
    validationSchema: Yup.object({
      info: Yup.string(),
      chat: Yup.string(),
      currency: Yup.string().required("Required"),
      maxPlayers: Yup.number()
        .typeError("Please enter a valid number")
        .required("Required"),
      fee: Yup.number()
        .typeError("Please enter a valid number")
        .required("Required"),
    }),
    onSubmit: () => {},
  });

  const handleCashChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsCash(event.target.checked);
  };

  const handleOnlinePaymentChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked === true) {
      if ((stripeAccount?.details_submitted == true) == true) {
        setIsOnlinePayment(event.target.checked);
        setShowFeeCalc(event.target.checked);
      } else {
        setShowStripeButton(true);
      }
    } else {
      setIsOnlinePayment(event.target.checked);
      setShowFeeCalc(event.target.checked);
    }

    const newGame = {
      ...form.values, // Spread the existing form values
      accept_cash_payment: isCash, // Add the accept_cash_payment property
      accept_online_payment: isBankTransfer, // Add the accept_online_payment property
      accept_app_payment: isOnlinePayment, // Add the accept_app_payment property
    };

    const updatedSecondForm = {
      ...game.secondForm,
      ...newGame, // Add the newGame properties to secondForm
    };

    const updatedGame = {
      ...game,
      secondForm: updatedSecondForm, // Update the secondForm property with the new values
    };
    localStorage.setItem("gameToBeCreated", JSON.stringify(updatedGame));
  };

  const handleAvailableOnMarketplaceChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsAvailableOnMarketplace(event.target.checked);
  };

  const handleBankTransferChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsBankTransfer(event.target.checked);
  };

  const handleDateChange = (date: Dayjs | null) => {
    formik.setFieldValue("date", date);
  };

  const handleNext = () => {
    setContentRender({ firstForm: false, secondForm: true });

    localStorage.setItem(
      "gameToBeCreated",
      JSON.stringify({
        ...game,
        firstForm: formik.values,
      })
    );
  };

  const fetchLocation = async (loc: string) => {
    const res = await getLocation({ location: loc, sessionToken });
    if (res?.meta.requestStatus === "fulfilled") {
      setLocationSuggestions(res?.payload?.suggestions);
    }
  };

  const retrieveAddress = async (id: string) => {
    const res = await retrieveLocation({ mapboxId: id, sessionToken });
    if (res?.meta.requestStatus === "fulfilled") {
      formik.setFieldValue("address", res?.payload?.features[0]);
    }
  };

  const handleBack = () => {
    if (contentRender.secondForm) {
      setContentRender({
        firstForm: true,
        secondForm: false,
      });
    }
    if (contentRender.firstForm) {
      navigate(location?.state?.from || "/home");
      localStorage.removeItem("gameToBeCreated");
    }
  };

  const hasStripeConnectAccount = async () => {
    const response = await userHasStripeAccount();
    setStripeAccount(response?.payload);
  };

  const onSuggestionClick = (location: any) => {
    formik.setFieldValue("location", location?.name);
    retrieveAddress(location?.mapbox_id);
  };

  const isOnePaymentMethodEnabled = () => {
    if (isCash || isBankTransfer || isOnlinePayment) {
      return true;
    } else {
      return false;
    }
  };

  const isCreateGame = () => {
    return form.isValid && isOnePaymentMethodEnabled();
  };

  const handleGameCreation = async () => {
    const startDate = new Date(formik.values.date!);
    const body = {
      title: formik.values.title,
      description: form.values.info,
      address: formik.values.address,
      location: formik.values.location,
      start_date: startDate.toISOString(),
      end_date: new Date(
        startDate.getTime() + Number(formik.values.duration?.value) * 60 * 1000
      ).toISOString(),
      fee: form.values.fee,
      currency: form.values.currency,
      max_players: form.values.maxPlayers,
      chat_link: form.values.chat,
      accept_cash_payment: isCash,
      available_on_marketplace: isAvailableOnMarketplace,
      accept_online_payment: isBankTransfer,
      accept_app_payment: isOnlinePayment,
    };
    const res = await createGame(body);
    if (res?.meta.requestStatus === "fulfilled") {
      localStorage.removeItem("gameToBeCreated");
      setAlert({
        alert: {
          isVisible: true,
          message: `Game created`,
          type: "success",
        },
      });
      navigate("/home");
    }
  };

  useEffect(() => {
    hasStripeConnectAccount();
  }, []);

  useEffect(() => {
    if (localStorage.getItem("gameToBeCreated")) {
      const game = JSON.parse(localStorage.getItem("gameToBeCreated")!);
      handleDateChange(dayjs(game?.firstForm?.date));
      setIsCash(game?.secondForm?.accept_cash_payment);
      setIsBankTransfer(game?.secondForm?.accept_online_payment);
      setIsOnlinePayment(game?.secondForm?.accept_app_payment);
    }
  }, []);

  useEffect(() => {}, [form.errors, formik.errors]);

  return (
    <Layout
      isLayout
      HeaderChildren={
        <Header title="Create new game" handleBack={handleBack} />
      }
    >
      <Content>
        <Form action="">
          {contentRender.firstForm ? (
            <FirstForm>
              <FormWrapper>
                <TextField
                  placeholder={"Title"}
                  name="title"
                  fullWidth
                  label="Title"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  errorMessage={formik.errors.title as string}
                  touched={formik.touched.title as boolean}
                />
                <LocationSearchBox
                  placeholder={"Location"}
                  name="location"
                  fullWidth
                  label="Location"
                  value={formik.values.location}
                  onChange={(e) => {
                    formik.setFieldValue("location", e.target.value);
                    if (e.target.value.length > 2)
                      fetchLocation(e.target.value);
                  }}
                  onSuggestionClick={onSuggestionClick}
                  data={locationSuggestions}
                  onBlur={formik.handleBlur}
                  errorMessage={formik.errors.location as string}
                  touched={formik.touched.location as boolean}
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoItem>
                    <MobileDateTimePicker
                      sx={{
                        "& .MuiInputBase-input": {
                          height: "auto",
                        },
                      }}
                      label={"Date & Time"}
                      onChange={handleDateChange}
                      value={formik.values.date}
                      disablePast
                    />
                  </DemoItem>
                </LocalizationProvider>
                <DropDown
                  name="duration"
                  placeholder="Pick a duration"
                  options={[
                    { label: "1hr", value: "60" },
                    { label: "1.5hrs", value: "90" },
                    { label: "2hrs", value: "120" },
                  ]}
                  onChange={(e, option) =>
                    formik.setFieldValue("duration", option)
                  }
                  defaultValue={formik.values.duration}
                  onBlur={formik.handleBlur}
                  errorMessage={formik.errors.duration as string}
                  touched={formik.touched.duration as boolean}
                />
              </FormWrapper>
              <Box m={2}>
                <Button
                  iscta
                  isDisabled={!(formik.isValid && formik.dirty)}
                  text="Fee, numbers & info"
                  onClick={handleNext}
                  color="primary"
                  endIcon={<RightArrow />}
                  textColor={"#FFF"}
                  type={"button"}
                />
              </Box>
            </FirstForm>
          ) : (
            <SecondForm>
              <FormWrapper>
                <TextField
                  placeholder={"Fee"}
                  name="fee"
                  fullWidth
                  label="Fee"
                  type="number"
                  value={form.values.fee.toString()}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  errorMessage={form.errors.fee as string}
                  touched={form.touched.fee as boolean}
                />
                <DropDown
                  name="currency"
                  placeholder="Currency"
                  options={currencies}
                  defaultValue={findOptionByValue(
                    currencies,
                    form.values.currency
                  )}
                  onChange={(e, option) =>
                    form.setFieldValue("currency", option?.value)
                  }
                  onBlur={form.handleBlur}
                  errorMessage={form.errors.currency as string}
                  touched={form.touched.currency as boolean}
                />
                <PaymentOptions>
                  <SwitchWrapper
                    label="Accept cash"
                    checked={isCash}
                    handleChange={handleCashChange}
                  />
                  <SwitchWrapper
                    label="Accept bank transfer"
                    checked={isBankTransfer}
                    handleChange={handleBankTransferChange}
                  />
                  <SwitchWrapper
                    label="Accept In-App Payment"
                    checked={isOnlinePayment}
                    handleChange={handleOnlinePaymentChange}
                  />
                  <TransactionFeeDisplay
                    getGameTransactionFee={getGameTransactionFee}
                    currency={form.values.currency}
                    fee={form.values.fee}
                    game_id={undefined}
                    isOpen={showFeeCalc}
                  />
                </PaymentOptions>
                <TextField
                  placeholder={"eg: 5"}
                  name="maxPlayers"
                  fullWidth
                  type="number"
                  label="Max number of players"
                  value={form.values.maxPlayers.toString()}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  errorMessage={form.errors.maxPlayers as string}
                  touched={form.touched.maxPlayers as boolean}
                />
                <TextField
                  placeholder={"eg: https://chat.whatsapp.com/12345678"}
                  name="chat"
                  fullWidth
                  label="Paste link to chat here"
                  value={form.values.chat}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  errorMessage={form.errors.chat as string}
                  touched={form.touched.chat as boolean}
                />
                <TextField
                  placeholder={"You should bring ..."}
                  name="info"
                  fullWidth
                  multiline
                  rows={4}
                  label="Additional information"
                  value={form.values.info}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  errorMessage={form.errors.info as string}
                  touched={form.touched.info as boolean}
                />
                <PaymentOptions>
                  <SwitchWrapper
                    label="Available on marketplace"
                    checked={isAvailableOnMarketplace}
                    handleChange={handleAvailableOnMarketplaceChange}
                  />
                </PaymentOptions>
              </FormWrapper>
              <Button
                iscta
                isDisabled={!isCreateGame()}
                text="Create & Publish"
                onClick={handleGameCreation}
                color="primary"
                textColor={"#FFF"}
                type={"button"}
              />
              <StripeModal
                modalTitle="Payments Account Required"
                modalButtonText="Create Payments Account"
                handleClose={() => {
                  setShowStripeButton(false);
                  if (stripeAccount?.details_submitted == true) {
                    setIsOnlinePayment(true);
                  } else {
                    setIsOnlinePayment(false);
                  }
                }}
                isOpen={showStripeButton}
              />
            </SecondForm>
          )}
        </Form>
      </Content>
    </Layout>
  );
};

const mapDispatchToProps = {
  setUser: homeActions.setUser,
  createGame: gameActions.createGame,
  getLocation: gameActions.getLocation,
  getGameTransactionFee: gameActions.getGameTransactionFee,
  retrieveLocation: gameActions.retrieveLocation,
  setAlert: LoaderSnackbarWrapperActions.setGlobalAppearances,
  userHasStripeAccount: transactionsActions.userHasStripeAccount,
};

const connector = connect(undefined, mapDispatchToProps);
const GameRedux = connector(WithAnalytics(Game));

export { GameRedux as Game };
